import React, { useState, useEffect, useRef } from 'react';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../Videoapi/Videoapi';
import './Videoapi.css';

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', planPrice: 499, planDays: 29 });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileRef = useRef();

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await getCategories();
      setCategories(res.data.data || []);
    } catch { showToast('Failed to load categories', 'error'); }
    finally { setLoading(false); }
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const openCreate = () => {
    setEditItem(null);
    setForm({ name: '', description: '', planPrice: 499, planDays: 29 });
    setImageFile(null); setImagePreview('');
    setModal('create');
  };

  const openEdit = (cat) => {
    setEditItem(cat);
    setForm({ name: cat.name, description: cat.description || '', planPrice: cat.planPrice, planDays: cat.planDays });
    setImageFile(null);
    setImagePreview(cat.image?.url || '');
    setModal('edit');
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return showToast('Name is required', 'error');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('description', form.description);
      fd.append('planPrice', form.planPrice);
      fd.append('planDays', form.planDays);
      if (imageFile) fd.append('image', imageFile);
      if (modal === 'create') { await createCategory(fd); showToast('Category created!'); }
      else { await updateCategory(editItem._id, fd); showToast('Category updated!'); }
      setModal(null);
      fetchCategories();
    } catch (err) { showToast(err.response?.data?.message || 'Error saving', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCategory(id);
      showToast('Category deleted');
      setConfirmDelete(null);
      fetchCategories();
    } catch { showToast('Failed to delete', 'error'); }
  };

  return (
    <div className="cat-root">
      <div className="cat-header">
        <div>
          <h1 className="cat-header-title">📂 Video Categories</h1>
          <p className="cat-header-sub">{categories.length} active categories</p>
        </div>
        <button className="btn-primary" onClick={openCreate}>＋ New Category</button>
      </div>

      <div className="cat-content">
        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : categories.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📁</div>
            <p>No categories yet. Create your first one!</p>
          </div>
        ) : (
          <div className="cat-grid">
            {categories.map(cat => (
              <div className="cat-card" key={cat._id}>
                <div className="cat-card-img">
                  {cat.image?.url ? <img src={cat.image.url} alt={cat.name} /> : '🎬'}
                </div>
                <div className="cat-card-body">
                  <h3 className="cat-card-name">{cat.name}</h3>
                  <p className="cat-card-desc">{cat.description || 'No description'}</p>
                  <div className="cat-card-meta">
                    <span className="badge badge-sky">₹{cat.planPrice}</span>
                    <span className="badge badge-sky">{cat.planDays} days</span>
                    <span className={`badge ${cat.isActive ? 'badge-green' : 'badge-red'}`}>
                      {cat.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="cat-card-actions">
                    <button className="btn-edit" onClick={() => openEdit(cat)}>✏️ Edit</button>
                    <button className="btn-danger" onClick={() => setConfirmDelete(cat)}>🗑 Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal">
            <h2 className="modal-title">{modal === 'create' ? '➕ New Category' : '✏️ Edit Category'}</h2>
            <div className="form-group">
              <label className="form-label">Category Name *</label>
              <input className="form-input" placeholder="e.g. Meditation" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-input" rows={3} placeholder="Brief description..." value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Plan Price (₹)</label>
                <input className="form-input" type="number" value={form.planPrice}
                  onChange={e => setForm(f => ({ ...f, planPrice: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Plan Days</label>
                <input className="form-input" type="number" value={form.planDays}
                  onChange={e => setForm(f => ({ ...f, planDays: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Category Image</label>
              <div className={`file-upload-area ${imagePreview ? 'has-file' : ''}`}
                onClick={() => fileRef.current.click()}>
                {imagePreview ? (
                  <><img src={imagePreview} alt="preview" className="file-preview" />
                    <p className="upload-hint-success">✅ Image selected — click to change</p></>
                ) : (
                  <><div className="upload-icon">🖼️</div>
                    <p className="upload-hint">Click to upload image (max 5MB)</p></>
                )}
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary-dark" onClick={handleSubmit} disabled={saving}>
                {saving ? '⏳ Saving...' : modal === 'create' ? '✅ Create' : '✅ Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <div className="confirm-icon">🗑️</div>
            <h3 className="confirm-title">Delete Category?</h3>
            <p className="confirm-text">Delete <strong>{confirmDelete.name}</strong>? This cannot be undone.</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => handleDelete(confirmDelete._id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`toast ${toast.type}`}>{toast.type === 'success' ? '✅' : '❌'} {toast.msg}</div>}
    </div>
  );
}